/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import type { ComponentProps, ReactElement } from 'react';
import { cssMap } from '@compiled/react';

import { jsx } from '@atlaskit/css';
import { useSmartCardContext } from '@atlaskit/link-provider/use-smart-card-context';
import {
	DEFAULT_EMBED_CARD_HEIGHT,
	DEFAULT_EMBED_CARD_WIDTH,
} from '@atlaskit/editor-shared-styles/constants';
import type { EmbedCard as OriginalEmbedCard } from '@atlaskit/renderer/nodes';
import { token } from '@atlaskit/tokens';

import type { LightNodeProps } from '../LightNodeObserver.ts';
import { InlineCard } from './InlineCard.tsx';

const styles = cssMap({
	frame: {
		maxWidth: '100%',
		boxSizing: 'border-box',
		marginBlock: token('space.300'),
		marginInline: 'auto',
		backgroundColor: token('elevation.surface.sunken'),
		borderRadius: token('radius.medium'),
		overflow: 'hidden',
	},
	link: { padding: token('space.200') },
	fallback: {
		backgroundColor: token('elevation.surface'),
		border: `${token('border.width')} solid ${token('color.border')}`,
	},
	start: { marginInlineStart: token('space.0') },
	end: { marginInlineEnd: token('space.0') },
	left: { float: 'left', marginInlineEnd: token('space.150') },
	right: { float: 'right', marginInlineStart: token('space.150') },
});
const ignoreInlineRef = () => {};
const dimension = (value: number | undefined, fallback: number) =>
	value && Number.isFinite(value) && value > 0 ? value : fallback;

/** Reserve embed geometry without mounting an iframe or resolving its preview. */
export const EmbedCard = ({
	lightNodeRef,
	originalWidth,
	originalHeight,
	width,
	layout,
	url,
	eventHandlers,
	onSetLinkTarget,
}: ComponentProps<typeof OriginalEmbedCard> & LightNodeProps): ReactElement => {
	const { value: context } = useSmartCardContext();
	const isFallback = Boolean(
		url &&
		context?.store.getState()[url]?.status === 'resolved' &&
		!context.extractors.getPreview(url, 'web'),
	);
	const heightOnly = Boolean(originalHeight && !originalWidth);
	const intrinsicWidth = dimension(originalWidth, DEFAULT_EMBED_CARD_WIDTH);
	const intrinsicHeight = dimension(originalHeight, DEFAULT_EMBED_CARD_HEIGHT);
	const wrapped = layout === 'wrap-left' || layout === 'wrap-right';
	const frameWidth =
		width && Number.isFinite(width) && width > 0
			? `${width}%`
			: layout === 'wide' || layout === 'full-width'
				? '100%'
				: wrapped
					? `min(50%, ${intrinsicWidth}px)`
					: `${intrinsicWidth}px`;
	return (
		<div
			ref={lightNodeRef}
			css={[
				styles.frame,
				isFallback && styles.fallback,
				layout === 'align-start' && styles.start,
				layout === 'align-end' && styles.end,
				layout === 'wrap-left' && styles.left,
				layout === 'wrap-right' && styles.right,
			]}
			data-embed-card
			data-layout={layout}
			data-card-url={url}
			// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Embed dimensions come from runtime ADF attributes.
			style={{
				// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Runtime ADF width.
				width: frameWidth,
				aspectRatio:
					isFallback || heightOnly ? undefined : `${intrinsicWidth} / ${intrinsicHeight}`,
				// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Runtime ADF height.
				height: !isFallback && heightOnly ? intrinsicHeight : undefined,
			}}
		>
			<div css={styles.link}>
				<InlineCard
					url={url}
					eventHandlers={eventHandlers}
					onSetLinkTarget={onSetLinkTarget}
					lightNodeRef={ignoreInlineRef}
				/>
			</div>
		</div>
	);
};
